<script setup lang="ts">
    import { dateOnlyFromApi } from '@shared/utils/formatDateTime';
    import { onMounted, reactive } from 'vue';
    import { bsAlert, AlertTypes } from '@shared/components/BsAlerts';
    import {
        getCategorized,
        getIndustries,
        saveClientCompanyProperty,
        saveSubscriptionProperty,
        CategorizedSubscriptions,
        Industry,
        PayingSubscription,
    } from './SubscriptionsDashboard.access';

    const state = reactive<{
        data: CategorizedSubscriptions | null;
        industries: Industry[];
        loading: boolean;
        activeTab: 'paying' | 'cancelled';
    }>({
        data: null,
        industries: [],
        loading: false,
        activeTab: 'paying',
    });

    onMounted(async () => {
        state.loading = true;
        try {
            [state.data, state.industries] = await Promise.all([getCategorized(), getIndustries()]);
        } catch {
            bsAlert({ type: AlertTypes.Danger, title: 'Error', content: 'Failed to load subscriptions dashboard' });
        } finally {
            state.loading = false;
        }
    });

    async function saveIndustry(sub: PayingSubscription, event: Event): Promise<void> {
        const el = event.target as HTMLSelectElement;
        const value = el.value || null;
        sub.ClientCompany.IndustryId = value;
        el.disabled = true;
        try {
            await saveClientCompanyProperty(sub.ClientCompany.ClientCompanyId, 'IndustryId', value);
        } catch {
            bsAlert({ type: AlertTypes.Danger, title: 'Error', content: 'Failed to save industry' });
        } finally {
            el.disabled = false;
        }
    }

    async function savePaymentMethod(sub: PayingSubscription, event: Event): Promise<void> {
        const el = event.target as HTMLSelectElement;
        const value = Number(el.value);
        sub.DefaultPaymentMethod = value;
        el.disabled = true;
        try {
            await saveSubscriptionProperty(sub.ServiceSubscriptionId, 'DefaultPaymentMethod', value);
        } catch {
            bsAlert({ type: AlertTypes.Danger, title: 'Error', content: 'Failed to save payment method' });
        } finally {
            el.disabled = false;
        }
    }

    async function saveRenewalPeriod(sub: PayingSubscription, event: Event): Promise<void> {
        const el = event.target as HTMLSelectElement;
        const value = Number(el.value);
        sub.RenewalPeriod = value;
        el.disabled = true;
        try {
            await saveSubscriptionProperty(sub.ServiceSubscriptionId, 'RenewalPeriod', value);
        } catch {
            bsAlert({ type: AlertTypes.Danger, title: 'Error', content: 'Failed to save renewal period' });
        } finally {
            el.disabled = false;
        }
    }

    async function savePaidUntil(sub: PayingSubscription, event: Event): Promise<void> {
        const el = event.target as HTMLInputElement;
        el.disabled = true;
        try {
            await saveSubscriptionProperty(sub.ServiceSubscriptionId, 'PaidUntil', sub.PaidUntil);
        } catch {
            bsAlert({ type: AlertTypes.Danger, title: 'Error', content: 'Failed to save paid until date' });
        } finally {
            el.disabled = false;
        }
    }

    function isPastDate(dateStr: string | null): boolean {
        if (!dateStr) return false;
        return new Date(dateStr) < new Date();
    }

    function formatDate(dateStr: string | null): string {
        if (!dateStr) return '';
        return new Date(dateStr).toLocaleDateString();
    }

    function toDateInputValue(dateStr: string | null): string {
        return dateOnlyFromApi(dateStr);
    }
</script>

<template>
    <h1 class="main-title">Subscriptions Dashboard</h1>

    <div v-if="state.loading">Loading...</div>
    <div v-else-if="state.data">
        <ul class="tabs list-unstyled">
            <li :class="{ active: state.activeTab === 'paying' }">
                <a href="#" @click.prevent="state.activeTab = 'paying'">
                    Paying ({{ state.data.Paying.length }})
                </a>
            </li>
            <li :class="{ active: state.activeTab === 'cancelled' }">
                <a href="#" @click.prevent="state.activeTab = 'cancelled'">
                    Cancelled ({{ state.data.Cancelled.length }})
                </a>
            </li>
        </ul>

        <div class="tab-content">
            <div v-if="state.activeTab === 'paying'" class="table-responsive">
                <table class="table table-hover">
                    <thead>
                        <tr>
                            <th>#</th>
                            <th>Company</th>
                            <th>Subscription</th>
                            <th>Status</th>
                            <th>Monthly price</th>
                            <th>Discount</th>
                            <th>Industry</th>
                            <th>Payment method</th>
                            <th>Renewal period</th>
                            <th>Renewed until</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(sub, index) in state.data.Paying" :key="sub.ServiceSubscriptionId">
                            <td class="text-nowrap">{{ index + 1 }}</td>
                            <td>{{ sub.ClientCompany.Name }}</td>
                            <td>{{ sub.ServiceSubscriptionTier.Name }} {{ sub.ServiceLevel.Name }}</td>
                            <td>{{ sub.Status === 5 ? 'Cancelled' : '' }}</td>
                            <td>{{ (sub.MonthlyPrice + sub.CurrentMonthlyAddonPrice).toFixed(2) }} {{ sub.ServiceLevelPrice.CurrencyCode }}</td>
                            <td>{{ sub.MonthlyTotalDiscount }} {{ sub.ServiceLevelPrice.CurrencyCode }}</td>
                            <td>
                                <select
                                    :value="sub.ClientCompany.IndustryId"
                                    class="form-control"
                                    @change="saveIndustry(sub, $event)">
                                    <option value="">Choose...</option>
                                    <option
                                        v-for="industry in state.industries"
                                        :key="industry.IndustryId"
                                        :value="industry.IndustryId">
                                        {{ industry.Name }}
                                    </option>
                                </select>
                            </td>
                            <td>
                                <select
                                    :value="sub.DefaultPaymentMethod"
                                    class="form-control"
                                    @change="savePaymentMethod(sub, $event)">
                                    <option :value="1">Credit card</option>
                                    <option :value="2">Invoice</option>
                                    <option :value="3">Paper invoice</option>
                                    <option :value="4">E-invoice</option>
                                </select>
                            </td>
                            <td>
                                <select
                                    :value="sub.RenewalPeriod"
                                    class="form-control"
                                    @change="saveRenewalPeriod(sub, $event)">
                                    <option :value="1">Monthly</option>
                                    <option :value="3">Quarterly</option>
                                    <option :value="12">Yearly</option>
                                </select>
                            </td>
                            <td>
                                <input
                                    type="date"
                                    class="form-control"
                                    :value="toDateInputValue(sub.PaidUntil)"
                                    :style="{ color: isPastDate(sub.PaidUntil) ? 'red' : 'inherit' }"
                                    @change="sub.PaidUntil = ($event.target as HTMLInputElement).value; savePaidUntil(sub, $event)" />
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div v-if="state.activeTab === 'cancelled'" class="table-responsive">
                <table class="table table-hover">
                    <thead>
                        <tr>
                            <th>Company</th>
                            <th>Subscription</th>
                            <th>Monthly price</th>
                            <th>Discount</th>
                            <th>Cancelled</th>
                            <th>Ends</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="sub in state.data.Cancelled" :key="sub.ServiceSubscriptionId">
                            <td>{{ sub.ClientCompany.Name }}</td>
                            <td>{{ sub.ServiceSubscriptionTier.Name }} {{ sub.ServiceLevel.Name }}</td>
                            <td>{{ (sub.MonthlyPrice + sub.CurrentMonthlyAddonPrice).toFixed(2) }} {{ sub.ServiceLevelPrice.CurrencyCode }}</td>
                            <td>{{ sub.MonthlyTotalDiscount }} {{ sub.ServiceLevelPrice.CurrencyCode }}</td>
                            <td>{{ formatDate(sub.Cancelled) }}</td>
                            <td>{{ formatDate(sub.End) }}</td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
    export default {
        name: 'SubscriptionsDashboard',
    };
</script>

<style></style>
